import { TEN_BILLION } from "@finway-group/shared/lib/consts"
import { Employee, Expense, ExpensePaymentFlowInformationEnum, InvoiceInterface } from "@finway-group/shared/lib/models"
import { Rule } from "antd/lib/form"
import { parseCurrencyInput } from "Components/currencyInput/config"
import i18n from "i18next"
import moment from "moment"
import { isNotSet } from "Shared/utils/helpers"

export const ExpenseRules = (expense?: Expense, employees?: Employee[], invoices?: InvoiceInterface[]): { [key: string]: Rule[] } => ({
    totalNetPrice: [
        {
            validator: (_rule: any, inputValue: string) => {
                let value = 0
                inputValue = inputValue ? inputValue.toString() : ""
                if (inputValue !== "") {
                    value = parseCurrencyInput(inputValue)
                    if (value >= TEN_BILLION) {
                        return Promise.reject(new Error(i18n.t("validation:below.number_short", { max: "10B" })))
                    }
                }

                return Promise.resolve()
            },
        },
    ],
    quantity: [
        {
            validator: (_rule: any, value: number | string) => {
                if (!isNotSet(value)) {
                    if (value <= 0) {
                        return Promise.reject(new Error(i18n.t("validation:greater.number_short", { min: 0 })))
                    }
                    return Promise.resolve()
                }

                return Promise.reject(new Error(i18n.t("validation:greater.number_short", { min: 0 })))
            },
        },
    ],
    name: [{ required: true, message: i18n.t("validation:required"), validateTrigger: "blur" }],
    subscriptionRate: [
        {
            validator: (_rule: any, inputValue: string) => {
                let value = 0
                inputValue = inputValue ? inputValue.toString() : ""
                if (inputValue !== "") {
                    value = parseCurrencyInput(inputValue)
                    if (value <= 0) {
                        return Promise.reject(new Error(i18n.t("validation:greater.number_short", { min: 0 })))
                    } else if (value >= TEN_BILLION) {
                        return Promise.reject(new Error(i18n.t("validation:below.number_short", { max: "10B" })))
                    }

                    return Promise.resolve()
                }

                return Promise.reject(new Error(i18n.t("validation:greater.number_short", { min: 0 })))
            },
        },
    ],
    link: [{ type: "url", message: i18n.t("validation:url"), validateTrigger: "blur" }],
    costCenter: [{ required: true, message: i18n.t("validation:required"), validateTrigger: "blur" }],
    approvalNeededBy: [
        { required: true, message: i18n.t("validation:required"), validateTrigger: "blur" },
        {
            validator: (_rule: any, value: string) => {
                const employee = employees?.find((employee) => employee.id === value)
                if (employee?.activeCompanyProfile.deleted) {
                    return Promise.reject(i18n.t("validation:user_deleted"))
                }
                return Promise.resolve()
            },
        },
    ],
    requestedBy: [{ required: true, message: i18n.t("validation:required"), validateTrigger: "blur" }],
    vendor: [{ required: true, message: i18n.t("validation:required"), validateTrigger: "blur" }],
    dateCancellationDue: [
        { type: "object", required: true, message: i18n.t("validation:required"), validateTrigger: "blur" },
        {
            validator: (_rule: any, value: moment.Moment) => {
                if (expense?.datePurchased && value) {
                    if (value.isSameOrBefore(expense?.datePurchased)) {
                        return Promise.reject(
                            i18n.t("validation:date_after", {
                                attribute: i18n.t("input:request.date_cancellation_due"),
                                compare: i18n.t("input:request.performance_date"),
                            })
                        )
                    }
                }

                return Promise.resolve()
            },
        },
    ],
    datePurchased: [
        { type: "object", required: true, message: i18n.t("validation:required"), validateTrigger: "blur" },
        {
            validator: (_rule: any, value: moment.Moment) => {
                if (expense?.dateCancellationDue && value) {
                    if (value.isSameOrAfter(expense?.dateCancellationDue)) {
                        return Promise.reject(
                            i18n.t("validation:date_before", {
                                attribute: i18n.t("input:request.performance_date"),
                                compare: i18n.t("input:request.date_cancellation_due"),
                            })
                        )
                    }
                }

                return Promise.resolve()
            },
        },
    ],
    invoiceNumber: [
        {
            validator: (_rule: any, value: string) => {
                if (expense?.isAtLeastApproved() && expense.paymentFlowInformation !== ExpensePaymentFlowInformationEnum.REPORTING_ONLY && !value?.trim()) {
                    return Promise.reject(i18n.t("validation:required_short"))
                }

                if (value) {
                    // DATEV limit
                    if (value.length > 36) {
                        return Promise.reject(i18n.t("validation:string_too_long"))
                    }
                }

                return Promise.resolve()
            },
        },
    ],
    invoiceDate: [
        {
            validator: (_rule: any, value: moment.Moment) => {
                if (expense?.isAtLeastApproved() && expense.paymentFlowInformation !== ExpensePaymentFlowInformationEnum.REPORTING_ONLY && !value) {
                    return Promise.reject(i18n.t("validation:required_short"))
                }

                if (expense?.invoiceDueDate && value) {
                    if (value.isAfter(expense?.invoiceDueDate)) {
                        return Promise.reject(
                            i18n.t("validation:date_after", {
                                attribute: i18n.t("input:request.invoice_date"),
                                compare: i18n.t("input:request.invoice_due_date"),
                            })
                        )
                    }
                }

                return Promise.resolve()
            },
        },
    ],
    invoiceDueDate: [
        {
            validator: (_rule: any, value: moment.Moment) => {
                if (expense?.invoiceDate && value) {
                    if (value.isBefore(expense?.invoiceDate)) {
                        return Promise.reject(
                            i18n.t("validation:date_after", {
                                attribute: i18n.t("input:request.invoice_due_date"),
                                compare: i18n.t("input:request.invoice_date"),
                            })
                        )
                    }
                }

                return Promise.resolve()
            },
        },
    ],
    datePaidAt: [
        {
            validator: (_rule: any, value: moment.Moment) => {
                if (expense?.isSuccessfullyProcessed() && !value) {
                    return Promise.reject(i18n.t("validation:required"))
                }

                return Promise.resolve()
            },
        },
    ],
    invoiceUpload: [
        {
            validator: (_rule: any) => {
                if (!invoices || invoices.length === 0) {
                    return Promise.reject(i18n.t("validation:invoice_needed"))
                }
                return Promise.resolve()
            },
        },
    ],
    reasonToReject: [{ required: true, message: i18n.t("validation:required"), validateTrigger: "blur" }],
    merchant: [
        {
            validator: (_rule: any, value) => {
                if (!value?.trim()) {
                    return Promise.reject(i18n.t("validation:required"))
                }
                return Promise.resolve()
            },
        },
        { max: 50, message: i18n.t("validation:max.string", { max: 50 }) },
    ],
    hospitalityDate: [
        {
            validator: (_rule: any, value: moment.Moment) => {
                if (!value) {
                    return Promise.reject(i18n.t("validation:required"))
                }

                if (value.isAfter(moment().endOf("day"))) {
                    return Promise.reject(i18n.t("validation:cannot_be_in_future"))
                }

                return Promise.resolve()
            },
        },
    ],
    attendees: [
        {
            required: true,
            validator: (_rule: any, value: any[]) => {
                if (!value || value?.length < 1) {
                    return Promise.reject(i18n.t("validation:required"))
                }
                return Promise.resolve()
            },
        },
    ],
    reason: [{ required: true, message: i18n.t("validation:required"), validateTrigger: "blur" }],
    description: [
        {
            required: true,
            validator: (_rule: any, value) => {
                if (!value?.trim()) {
                    return Promise.reject(i18n.t("validation:required"))
                }
                return Promise.resolve()
            },
        },
    ],
})
